<template>
	<v-card tile elevation="0" style="height: 100%; overflow-y: hidden">
		<v-card-title class="text-left card-header fixed-card-title2">
			<v-row no-gutters>
				<v-col cols="10">
					<v-toolbar-title>{{ $t('edit-ccm-info') }}</v-toolbar-title>
				</v-col>
				<v-spacer />
				<v-icon @click="closeDrawer" class="white-icon">mdi-close</v-icon>
			</v-row>
		</v-card-title>
		<v-card-text class="pa-4 overflow-y-auto" style="height: calc(100vh - 221px)">
			<v-row>
				<v-col cols="12">
					<v-select
						:label="$t('provider')"
						v-model="selectedProvider"
						:items="doctors"
						:item-text="'fullName'"
						outlined
						return-object
						hide-details
						:menu-props="{ maxHeight: 199, offsetY: true }" />
				</v-col>
				<v-col cols="12">
					<v-select
						:label="$t('coordinator')"
						v-model="selectedCoordinator"
						:items="nurses"
						:item-text="'fullName'"
						outlined
						return-object
						hide-details
						:menu-props="{ maxHeight: 199, offsetY: true }" />
				</v-col>
				<v-col cols="12" v-if="!doctors?.length">
					<v-card color="primary" class="pa-2 white--text">
						<v-icon size="19" color="white">mdi-alert-circle-outline</v-icon>
						{{ $t('note') }}: {{ $t('please-assign-a-doctor-to-the-patient') }}
					</v-card>
				</v-col>
				<v-col cols="12" v-if="!nurses?.length">
					<v-card color="primary" class="pa-2 white--text">
						<v-icon size="19" color="white">mdi-alert-circle-outline</v-icon>
						{{ $t('note') }}: {{ $t('please-assign-a-nurse-to-the-patient') }}
					</v-card>
				</v-col>
			</v-row>
		</v-card-text>
		<v-container class="px-4">
			<v-row dense class="ma-0">
				<v-col cols="12">
					<InputTimeFormatter
						:inputValue="timeSpent"
						@update:inputValue="(val) => (timeSpent = val)"
						:dense="false"
						:inputLabel="$t('time-spent')" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="6" order="1">
					<v-btn @click="closeDrawer()" color="secondary" style="color: black" block>{{ $t('cancel') }}</v-btn>
				</v-col>
				<v-tooltip top v-if="!editButtonEnabled">
					<template v-slot:activator="{ on, attrs }">
						<v-col cols="6" v-bind="attrs" v-on="on" order="2">
							<v-btn disabled block>{{ $t('update') }}</v-btn>
						</v-col>
					</template>
					<span>{{ $t('fill-all-fields-warning') }}</span>
				</v-tooltip>
				<v-col cols="6" v-else order="2">
					<v-btn @click="updateRecord()" color="dark_purple" style="color: white" block>{{ $t('update') }}</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import InputTimeFormatter from '../Client/components/InputTimeFormatter.vue';
export default {
	name: 'CCMInfoEdit',
	data () {
		return {
			doctors: null,
			nurses: null,
			staff: null,
			selectedCoordinator: null,
			selectedProvider: null,
			selectedType: null,
			timeSpent: 0,
		};
	},
	computed: {
		...mapState({
			detailedUser: (state) => state.users.detailedUser,
			ccmRecordsByPatientId: (state) => state.ccmRecord.ccmRecordsByPatientId,
			currentRecord: (state) => state.ccmRecords.ccmRecord,
			userData: (state) => state.authentication.userData,
		}),
		editButtonEnabled () {
			return this.selectedCoordinator && this.selectedProvider && this.timeSpent && this.timeSpent <= 7200;
		},
	},
	async created () {
		this.staff = await this.getStaff();
		this.doctors = await this.filterDoctors();
		this.nurses = await this.filterNurses();
		this.selectedProvider = this.doctors.find((doctor) => doctor.id === this.currentRecord.providerId);
		this.selectedCoordinator = this.nurses.find((nurse) => nurse.id === this.currentRecord.coordinatorId);
	},
	methods: {
		...mapActions({
			updateCCMRecord: 'ccmRecords/updateCCMRecord',
		}),
		closeDrawer () {
			this.timeSpent = 0;
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', false);
			this.$store.commit('SET_EDIT_DETAILS_DRAWER', false);
		},
		filterDoctors () {
			return this.staff.filter((member) => member.roleId === 3 && member.status === 1);
		},
		filterNurses () {
			return this.staff.filter((member) => member.roleId === 4 && member.status === 1);
		},
		getStaff () {
			return this.detailedUser.userData.medicalTeamsData[0].membersList.map((member) => ({
				...member,
				fullName: `${member.firstName} ${member.lastName}`,
			}));
		},
		updateRecord () {
			const request = {
				id: this.currentRecord.id,
				body: {
					hospitalId: this.currentRecord.hospitalId,
					providerId: this.selectedProvider.id,
					coordinatorId: this.selectedCoordinator.id,
					lastModifiedBy: `${this.userData.firstName} ${this.userData.lastName}`,
					timeSpentSeconds: parseInt(this.timeSpent),
				},
			};
			this.$store.commit('ccmRecords/UPDATE_CCM_RECORD_IN_PATIENT_LIST', {
				id: this.currentRecord.id,
				providerId: this.selectedProvider.id,
				providerName: this.selectedProvider.fullName,
				coordinatorId: this.selectedCoordinator.id,
				coordinatorName: this.selectedCoordinator.fullName,
			});
			this.$store.commit('ccmRecords/UPDATE_CCM_RECORD', {
				providerId: this.selectedProvider.id,
				providerName: this.selectedProvider.fullName,
				coordinatorId: this.selectedCoordinator.id,
				coordinatorName: this.selectedCoordinator.fullName,
			});
			this.updateCCMRecord(request);
			this.closeDrawer();
		},
	},
	watch: {
		currentRecord: function () {
			this.selectedProvider = this.doctors.find((doctor) => doctor.id === this.currentRecord.providerId);
			this.selectedCoordinator = this.nurses.find((nurse) => nurse.id === this.currentRecord.coordinatorId);
			if (this.currentRecord.isComplex) {
				this.selectedType = 'complex';
			} else if (this.currentRecord.isNonComplex) {
				this.selectedType = 'non-complex';
			} else {
				this.selectedType = 'physician-npp-provided';
			}
		},
	},
	components: { InputTimeFormatter },
};
</script>
